/**
 * space_aar.ts — THE AFTER-ACTION RECORDS panel (space campaign slice C, 2026-09-30).
 *
 * The ops panel's THE CAMPAIGN section ends with this: the live war's HONEST window (a campaign the sweep opened late
 * keeps its real clock — how much war it really has, and whether its 15-minute retreat floor can still be reached),
 * then the recent wars' after-action records (`campaign_aar`, one per settled campaign, kept 30 days): where it was
 * fought vs what it decided, who won and WHY, every roster loss and its cause, what went home, and what each side's
 * receipts paid — "every payout has one authoritative cause" (report §11, P0-C). Structured after the Army's AAR (what
 * was supposed to happen, what happened, why) and EVE's killmail lesson: a public record says what it cannot prove.
 * Pure line builder (`aarLines`, harness-tested) + a DOM renderer on the `--ax-*` tokens; `<details>` rows stay open
 * across the panel's 1.2 s re-render. Its own lazy subscription (the record is public; a client asks for it only when
 * the ops panel is open).
 */
import { lateWindow, LATE_NOTICE_S, fnv1a64 } from '@shared/campaign_contract.js';
import { aarOpsLines, type AarOps } from './space_ops_lines.js'; // space campaign slice H: the operations' lines

export interface AarSummary {
  v: number; campaign: string; cyc: number; generation: number; rules: number; quality: string; sandbox: boolean;
  battlefield: { system: string; fort: string; name: string };
  front: string;
  open: { atS: number; endsAtS: number; lateS: number; warS: number; floorReachable: boolean };
  outcome: { winner: string; retreatSide: number; cause: string; rounds: number; settledAtS: number };
  sov: { system: string; delta: number; applied: boolean };
  sides: Array<{
    civ: string;
    committed: { caps: number; sk: number; fri: number; cor: number };
    lost: { caps: number; sk: number; fri: number; cor: number };
    survivors: { caps: number; sk: number; fri: number; cor: number };
    returned: { hulls: number; caps: number };
    receipts: { n: number; points: number; paid: number; pilots: number };
  }>;
  losses: Array<{ side: number; kind: string; slot: number; by: string; round: number; atS: number }>;
  lossesTotal: number;
  pay: { tx: string[]; txTotal: number };
  journal: { events: number; dropped: number };
  limits: string[];
  /** Slice H: the operations that supported the war (each settled once with it) and its escort extractions. */
  operations?: AarOps;
}
export interface AarLine { icon: string; text: string; tone?: 'good' | 'bad' | 'warn' | 'muted' | 'info' }

const SIDE = ['Concord', 'Compact'] as const;
const CIV = (civ: string): string => (civ === 'aurelian' ? 'Concord' : civ === 'borean' ? 'Compact' : civ);
const hhmm = (s: number): string => new Date(s * 1000).toISOString().slice(11, 16);
const mins = (s: number): string => (s >= 90 ? `${Math.round(s / 60)} min` : `${Math.round(s)} s`);

/** Why a life died, in words (the roster's `killedBy`). */
export function lossCause(by: string, round: number, me: string): string {
  if (by === 'round') return `to the line's attrition, minute ${round}`;
  if (by === 'debug') return 'struck from the roster by a lever';
  if (me && by === me) return 'to YOUR guns (your receipt)';
  return 'to a pilot\'s guns (a receipt)';
}

/** The record as lines a pilot reads (what was supposed to happen, what happened, why, what it paid). `mine` is the
 *  reader's own receipts for that war (their rows; the record itself never lists a pilot's). */
export function aarLines(s: AarSummary, me: string, mine?: { n: number; points: number; paid: bigint }): AarLine[] {
  const out: AarLine[] = [];
  out.push({ icon: '⚔', text: `Fought at ${s.battlefield.name} in the Commons Reach (${s.battlefield.system}) — it decided the front at ${s.front}.` });
  const lw = s.open; // recorded at the OPENING (a retreat later pulls endsAt — never re-derive the late window from it)
  if (lw.lateS >= LATE_NOTICE_S) out.push({ icon: '⏱', tone: 'warn', text: `Opened ${mins(lw.lateS)} late (${hhmm(s.open.atS)} UTC): a ${mins(lw.warS)} war${lw.floorReachable ? '' : ' — too short for the 15-minute retreat floor; only a line with no capitals could leave'}.` });
  else out.push({ icon: '⏱', text: `Opened on the clock at ${hhmm(s.open.atS)} UTC; ${s.outcome.rounds} minute-round${s.outcome.rounds === 1 ? '' : 's'} fought.` });
  const w = s.outcome.winner;
  if (w === 'draw') out.push({ icon: '⚖', text: 'A draw — neither line yielded and both held as many capitals at the window\'s end.' });
  else if (s.outcome.retreatSide >= 0) out.push({ icon: '⚑', tone: 'good', text: `The ${CIV(w)} held the field: the ${SIDE[s.outcome.retreatSide] ?? '?'} retreated — ${s.outcome.cause || 'its line broke'}.` });
  else out.push({ icon: '⚑', tone: 'good', text: `The ${CIV(w)} held more capitals when the window closed.` });
  if (s.sov.delta) out.push({ icon: '⇄', tone: s.sov.applied ? 'info' : 'muted', text: `${s.sov.system}'s control moved ${s.sov.delta > 0 ? '+' : ''}${s.sov.delta} toward the ${s.sov.delta > 0 ? 'Concord' : 'Compact'}${s.sov.applied ? '' : ' (a harness war: recorded, not applied)'}.` });
  else out.push({ icon: '⇄', tone: 'muted', text: 'No front moved (a draw moves nothing).' });
  for (let i = 0; i < s.sides.length; i++) {
    const d = s.sides[i];
    out.push({ icon: i === 0 ? '◆' : '◇', text: `${CIV(d.civ)}: committed ${d.committed.caps} capitals · ${d.committed.sk} gunships · ${d.committed.fri} frigates · ${d.committed.cor} corvettes; lost ${d.lost.caps} · ${d.lost.sk} · ${d.lost.fri} · ${d.lost.cor}; home to the store +${d.returned.hulls} hulls, +${d.returned.caps} capitals${s.sandbox ? ' (recorded)' : ''}.` });
  }
  const losses = [...s.losses].sort((a, b) => a.atS - b.atS);
  for (const l of losses) {
    const what = l.kind === 'capital' ? 'capital' : l.kind;
    out.push({ icon: '☄', tone: l.by === me && me ? 'good' : 'bad', text: `${SIDE[l.side] ?? '?'} ${what} #${l.slot + 1} fell at ${hhmm(l.atS)} — ${lossCause(l.by, l.round, me)}.` });
  }
  if (s.lossesTotal > losses.length) out.push({ icon: '…', tone: 'muted', text: `${s.lossesTotal - losses.length} more losses are counted, not listed (the record's 32 KB budget).` });
  for (let i = 0; i < s.sides.length; i++) {
    const r = s.sides[i].receipts;
    const won = (w === 'aurelian' && i === 0) || (w === 'borean' && i === 1);
    out.push({ icon: 'ⓒ', tone: r.paid > 0 ? 'good' : 'muted', text: r.n
      ? `${SIDE[i]} receipts: ${r.n} from ${r.pilots} pilot${r.pilots === 1 ? '' : 's'}, ${r.points} pts — ${won ? `paid ${r.paid} ⓒ (25 ⓒ a point, ≤ 40 pts a pilot)` : 'not paid: a losing or drawn side\'s receipts pay nothing'}.`
      : `${SIDE[i]} receipts: none filed.` });
  }
  if (s.pay.txTotal) out.push({ icon: '≡', tone: 'muted', text: `Paid through ${s.pay.txTotal} ledger transaction${s.pay.txTotal === 1 ? '' : 's'} (${s.pay.tx.slice(0, 4).join(', ')}${s.pay.txTotal > 4 ? ', …' : ''}).` });
  if (mine && mine.n) out.push({ icon: '★', tone: 'info', text: `Your receipts in this war: ${mine.n} (${mine.points} pts, ${mine.paid} ⓒ paid).` });
  if (s.operations) out.push(...aarOpsLines(s.operations)); // slice H: each operation's contribution and payout
  for (const lim of s.limits) out.push({ icon: '⚠', tone: 'muted', text: `Not proven by this record: ${lim}.` });
  out.push({ icon: '#', tone: 'muted', text: `Record v${s.v} · rules ${s.rules} · settlement ${s.generation} · ${s.quality === 'full' ? 'full journal' : s.quality === 'backfilled' ? 'rebuilt from the rows (no journal)' : 'journal began mid-war'} (${s.journal.events} events${s.journal.dropped ? `, ${s.journal.dropped} optional ones counted, not kept` : ''})${s.sandbox ? ' · a HARNESS war' : ''}.` });
  return out;
}

/** The live war's honest window, in one line (null when on time). */
export function liveWindowLine(openedAtS: number, endsAtS: number, nowS: number): AarLine | null {
  const lw = lateWindow(openedAtS, endsAtS);
  if (lw.lateS < LATE_NOTICE_S) return null;
  return { icon: '⏱', tone: 'warn', text: `This war opened ${mins(lw.lateS)} late (the sweep was away): it has ${mins(Math.max(0, endsAtS - nowS))} of a ${mins(lw.warS)} window left${lw.floorReachable ? '' : ' — too short for the 15-minute retreat floor, so only a line with no capitals can leave before the window closes'}.` };
}

// ── DOM (tokens only — THE TOKEN LAW) ────────────────────────────────────────
const TONE: Record<NonNullable<AarLine['tone']> | 'none', string> = {
  none: 'var(--ax-text)', good: 'var(--ax-good)', bad: 'var(--ax-bad)', warn: 'var(--ax-warn)', muted: 'var(--ax-muted)', info: 'var(--ax-info)',
};
const openKeys = new Set<string>();
const subscribed = new WeakSet<object>();
interface AarRowLike { aarKey: string; campaignId: bigint; summary: string; checksum: string; settledAt: { microsSinceUnixEpoch: bigint }; sandbox: boolean; winner: string; targetSystem: string; fortKey: string }
interface ConnLike {
  identity?: { toHexString(): string };
  db: Record<string, unknown>;
  subscriptionBuilder(): { subscribe(q: string[]): unknown };
}
interface CampLike { openedAt: { microsSinceUnixEpoch: bigint }; endsAt: { microsSinceUnixEpoch: bigint }; state: string }

function lineEl(l: AarLine): HTMLDivElement {
  const d = document.createElement('div');
  d.style.cssText = `font:11px var(--ax-mono);color:${TONE[l.tone ?? 'none']};margin:2px 0;display:flex;gap:6px;align-items:baseline;overflow-wrap:anywhere`;
  const ic = document.createElement('span');
  ic.textContent = l.icon;
  ic.setAttribute('aria-hidden', 'true');
  ic.style.cssText = 'flex:0 0 auto;width:1.2em;text-align:center';
  const tx = document.createElement('span');
  tx.textContent = l.text;
  d.append(ic, tx);
  return d;
}

/** ONE hook in SpaceModule.renderOps (THE CAMPAIGN section): the live war's honest window + the recent records. */
export function renderCampaignRecord(root: HTMLElement, conn: unknown, camp: CampLike | null, h: (txt: string) => HTMLElement): void {
  const c = conn as ConnLike;
  try {
    if (!subscribed.has(c)) { subscribed.add(c); c.subscriptionBuilder().subscribe(['SELECT * FROM campaign_aar']); }
  } catch { /* pre-publish */ }
  const nowS = Date.now() / 1000;
  if (camp && camp.state === 'engaged') { // while engaged endsAt is the window's own end (a retreat pulls it)
    const l = liveWindowLine(Number(camp.openedAt.microsSinceUnixEpoch / 1_000_000n), Number(camp.endsAt.microsSinceUnixEpoch / 1_000_000n), nowS);
    if (l) root.appendChild(lineEl(l));
  }
  root.appendChild(h('AFTER-ACTION RECORDS — what each war decided, and why'));
  let rows: AarRowLike[] = [];
  try { rows = [...(c.db.campaignAar as { iter(): Iterable<AarRowLike> }).iter()]; } catch { /* pre-publish */ }
  rows.sort((a, b) => (b.settledAt.microsSinceUnixEpoch > a.settledAt.microsSinceUnixEpoch ? 1 : b.settledAt.microsSinceUnixEpoch < a.settledAt.microsSinceUnixEpoch ? -1 : 0));
  if (!rows.length) { root.appendChild(lineEl({ icon: '…', tone: 'muted', text: 'No war has been settled under the record yet — each one writes its after-action record as it settles.' })); return; }
  const me = c.identity?.toHexString() ?? '';
  const mineOf = (id: bigint): { n: number; points: number; paid: bigint } => {
    const out = { n: 0, points: 0, paid: 0n };
    try {
      for (const r of (c.db.campaignReceipt as { iter(): Iterable<{ campaignId: bigint; identity: { toHexString(): string }; points: number; paidSov: bigint }> }).iter()) {
        if (r.campaignId === id && r.identity.toHexString() === me) { out.n++; out.points += r.points; out.paid += r.paidSov; }
      }
    } catch { /* pre-publish */ }
    return out;
  };
  for (const r of rows.slice(0, 8)) {
    let s: AarSummary | null = null;
    try { s = JSON.parse(r.summary) as AarSummary; } catch { s = null; }
    const det = document.createElement('details');
    det.dataset.aar = r.aarKey;
    det.open = openKeys.has(r.aarKey);
    det.addEventListener('toggle', () => { if (det.open) openKeys.add(r.aarKey); else openKeys.delete(r.aarKey); });
    det.style.cssText = 'border:1px solid var(--ax-line);border-radius:var(--ax-radius);margin:4px 0;padding:3px 6px;background:var(--ax-bg2)';
    const sum = document.createElement('summary');
    sum.style.cssText = 'cursor:pointer;font:11px var(--ax-mono);color:var(--ax-text)';
    const ago = Math.max(0, Math.round((nowS - Number(r.settledAt.microsSinceUnixEpoch / 1_000_000n)) / 60));
    const won = r.winner === 'draw' ? 'a draw' : `${CIV(r.winner)} held the field`;
    sum.textContent = `⚔ War #${r.campaignId}${r.sandbox ? ' (harness)' : ''} · ${s?.battlefield.name ?? r.fortKey} → ${r.targetSystem} · ${won} · ${ago < 90 ? `${ago} min ago` : `${Math.round(ago / 60)} h ago`}`;
    det.appendChild(sum);
    if (!s) det.appendChild(lineEl({ icon: '⚠', tone: 'bad', text: 'This record could not be read.' }));
    else {
      const intact = fnv1a64(r.summary) === r.checksum;
      for (const l of aarLines(s, me, mineOf(r.campaignId))) det.appendChild(lineEl(l));
      det.appendChild(lineEl({ icon: intact ? '✓' : '✗', tone: intact ? 'muted' : 'bad', text: `Checksum ${r.checksum} ${intact ? 'matches' : 'does NOT match'} the record.` }));
    }
    root.appendChild(det);
  }
}
